"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { updateLiveLocation } from "@/actions/chat";
import { useAuthState } from "@/hooks/use-auth-state";
import {
  MY_LIVE_LOCATIONS_KEY,
  useMyActiveLiveLocations,
} from "@/hooks/use-chat";
import {
  distanceMeters,
  LIVE_LOCATION_MIN_UPDATE_INTERVAL_MS,
} from "@/lib/chat/live-location";

/** Moving less than this between updates isn't news… */
const MIN_MOVE_M = 20;
/** …but a standing-still sharer still refreshes this often ("just now"). */
const HEARTBEAT_MS = 60_000;

type Fix = { lat: number; lng: number; accuracy: number | null };

/**
 * Keeps every live location the viewer is sharing up to date, from anywhere in
 * the app — leaving the chat doesn't stop the share. Mounted once in the root
 * layout; renders nothing, and holds no GPS watch unless a share is running.
 *
 * Runs while the app is running. A backgrounded iOS app is suspended, so its
 * card shows "Updated N min ago" rather than pretending to be live.
 */
export function LiveLocationSharer() {
  const { authStatus } = useAuthState();
  const queryClient = useQueryClient();
  const { data: shares = [] } = useMyActiveLiveLocations(
    authStatus === "authenticated",
  );
  // 0 until mounted: this renders in the root layout, and reading the clock
  // during render is an unstable prerender value under cacheComponents.
  // Shares come from the server already filtered to running ones, so the
  // first client pass treating them all as live is correct.
  const [now, setNow] = useState(0);
  useEffect(() => setNow(Date.now()), []);

  const active = useMemo(
    () => shares.filter((share) => share.liveUntil > now),
    [shares, now],
  );
  const activeRef = useRef(active);
  activeRef.current = active;
  const activeKey = active.map((share) => share.messageId).join(",");

  // Re-evaluate the moment the soonest share runs out.
  useEffect(() => {
    let soonest = Number.POSITIVE_INFINITY;
    for (const share of shares) {
      if (share.liveUntil > now) soonest = Math.min(soonest, share.liveUntil);
    }
    if (!Number.isFinite(soonest)) return;
    const timeoutId = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(soonest - Date.now() + 500, 2_147_000_000),
    );
    return () => window.clearTimeout(timeoutId);
  }, [shares, now]);

  useEffect(() => {
    if (!activeKey) return;
    let cancelled = false;
    let watchId: string | null = null;
    let clearWatch: ((id: string) => void) | null = null;
    let lastFix: Fix | null = null;
    let lastSent: (Fix & { at: number }) | null = null;
    let inFlight = false;

    const push = async (fix: Fix, force = false) => {
      if (inFlight || cancelled) return;
      const at = Date.now();
      if (lastSent) {
        const elapsed = at - lastSent.at;
        if (elapsed < LIVE_LOCATION_MIN_UPDATE_INTERVAL_MS) return;
        if (
          !force &&
          distanceMeters(lastSent, fix) < MIN_MOVE_M &&
          elapsed < HEARTBEAT_MS
        ) {
          return;
        }
      }
      inFlight = true;
      lastSent = { ...fix, at };
      try {
        const results = await Promise.all(
          activeRef.current.map((share) =>
            updateLiveLocation({
              messageId: share.messageId,
              lat: fix.lat,
              lng: fix.lng,
              accuracy: fix.accuracy ?? undefined,
            }),
          ),
        );
        // A share the server says is over (time up, stopped on another
        // device, left the group) drops out, and the watch with it.
        if (
          results.some(
            (result) => "data" in result && result.data?.active === false,
          )
        ) {
          void queryClient.invalidateQueries({
            queryKey: MY_LIVE_LOCATIONS_KEY,
          });
        }
      } catch {
        // Offline or a blip: the next fix or heartbeat tries again.
        lastSent = null;
      } finally {
        inFlight = false;
      }
    };

    void (async () => {
      try {
        const { Geolocation } = await import("@capacitor/geolocation");
        if (cancelled) return;
        clearWatch = (id) => void Geolocation.clearWatch({ id });
        const id = await Geolocation.watchPosition(
          { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 },
          (position) => {
            if (!position) return;
            lastFix = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy:
                typeof position.coords.accuracy === "number"
                  ? Math.round(position.coords.accuracy)
                  : null,
            };
            void push(lastFix);
          },
        );
        if (cancelled) void Geolocation.clearWatch({ id });
        else watchId = id;
      } catch {
        // No location access: the card's "Updated N min ago" says so.
      }
    })();

    const heartbeat = window.setInterval(() => {
      if (lastFix) void push(lastFix, true);
    }, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(heartbeat);
      if (watchId && clearWatch) clearWatch(watchId);
    };
  }, [activeKey, queryClient]);

  return null;
}
